/**
 * @file usePanelLeaveGuard.ts
 * @description Защита несохранённых правок панели конструктора (phase19.16;
 * ревью партии SB122): окно «Сохранить / Не сохранять / Остаться»
 * (`useBlockLeaveGuard`) регистрируется в конструкторе (`setGuard`), и его
 * спрашивают все уходы из панели — выбор другого блока, раздела, страницы и
 * свёртка колонки с панелью (`BuilderWorkspace.collapse` → `leave`).
 * Потребители — панель блока и «Настройки страницы».
 *
 * `passed` — переход уже разрешён этим окном (второе окно `UnsavedGuard`
 * не нужно).
 */

import { useEffect, useRef, type MutableRefObject } from "react";

import { useBuilder } from "../../components/site-builder/builder-context";
import { useBlockLeaveGuard, type BlockLeaveGuard } from "./useBlockLeaveGuard";

export interface PanelLeaveGuard {
  leave: BlockLeaveGuard;
  passed: MutableRefObject<boolean>;
}

/**
 * @param dirty есть несохранённые правки
 * @param save сохранить; `true` — сохранено (можно уходить)
 */
export function usePanelLeaveGuard(dirty: boolean, save: () => Promise<boolean>): PanelLeaveGuard {
  const { setGuard } = useBuilder();
  const leave = useBlockLeaveGuard(dirty, save);
  const passed = useRef(false);
  const guard = leave.guard;
  useEffect(() => {
    setGuard(async () => {
      const ok = await guard();
      if (ok) passed.current = true;
      return ok;
    });
    return () => setGuard(null);
  }, [setGuard, guard]);
  return { leave, passed };
}
